// ============================================================================
// Motion.
//
// Everything here is opt-in via data attributes, so markup stays declarative
// and a view never has to hand-wire an animation:
//
//   <div data-reveal>              fade + rise when scrolled into view
//   <div data-reveal="left">       directional variants
//   <span data-count="4250">       counts up when first seen
//   <button data-magnetic>         drifts toward the cursor
//   <div data-parallax="0.25">     moves slower than the scroll
//   <div data-tilt>                3D tilt toward the cursor
//   <div data-reels>               rotating deck of click-to-play reels
//   <video data-hero-video>        muted background loop, on screen only
//
// Two rules throughout:
//   1. prefers-reduced-motion disables all of it, and content must remain
//      fully visible and usable when it does. An animation that hides content
//      until a scroll event is a bug, not an effect.
//   2. Everything runs off IntersectionObserver or rAF — never a scroll
//      listener doing layout reads, which is what makes pages feel janky.
// ============================================================================

const reduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let revealObserver = null;
let countObserver = null;
const rafTargets = { parallax: [], magnetic: [], tilt: [] };
let rafId = null;
let pointer = { x: 0, y: 0 };

// ------------------------------------------------------------- reveal -----

function initReveal(root) {
  const items = root.querySelectorAll('[data-reveal]:not([data-reveal-done])');
  if (!items.length) return;

  if (reduced()) {
    items.forEach((el) => {
      el.setAttribute('data-reveal-done', '');
      el.classList.add('is-revealed');
    });
    return;
  }

  revealObserver ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const el = entry.target;

      // Stagger children of a group so a grid cascades instead of popping.
      const delay = Number(el.dataset.revealDelay ?? 0);
      setTimeout(() => el.classList.add('is-revealed'), delay);

      revealObserver.unobserve(el);
    }
  }, {
    // Fire slightly before the element reaches the viewport, so by the time
    // it is actually on screen the animation is already underway.
    rootMargin: '0px 0px -12% 0px',
    threshold: 0.08
  });

  items.forEach((el, i) => {
    el.setAttribute('data-reveal-done', '');
    // Auto-stagger siblings that did not set an explicit delay.
    if (el.dataset.revealDelay === undefined && el.parentElement?.dataset.revealGroup !== undefined) {
      el.dataset.revealDelay = String(Math.min(i * 70, 420));
    }
    revealObserver.observe(el);
  });
}

// -------------------------------------------------------- count-up --------

/**
 * Count a number up when it scrolls into view.
 *
 * Uses the element's rendered text as the format template so it works for
 * "$412.50", "4,250" and "12.5%" without each call site describing its format.
 */
function initCounters(root) {
  const items = root.querySelectorAll('[data-count]:not([data-count-done])');
  if (!items.length) return;

  if (reduced()) {
    items.forEach((el) => el.setAttribute('data-count-done', ''));
    return;
  }

  countObserver ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      runCount(entry.target);
      countObserver.unobserve(entry.target);
    }
  }, { threshold: 0.4 });

  items.forEach((el) => {
    el.setAttribute('data-count-done', '');
    countObserver.observe(el);
  });
}

function runCount(el) {
  const target = Number(el.dataset.count);
  if (!Number.isFinite(target)) return;

  const decimals = Number(el.dataset.countDecimals ?? 0);
  const prefix = el.dataset.countPrefix ?? '';
  const suffix = el.dataset.countSuffix ?? '';
  const duration = Number(el.dataset.countDuration ?? 1100);
  const start = performance.now();

  const format = (v) =>
    prefix +
    v.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }) +
    suffix;

  function frame(now) {
    const t = Math.min((now - start) / duration, 1);
    // easeOutExpo: fast start, long settle. Reads as "landing on" a number.
    const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    el.textContent = format(target * eased);
    if (t < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

// ------------------------------------------------------------- reels ------

/**
 * Auto-rotating 3D deck of creator reels.
 *
 * JS sets one number per card — `--o`, the signed distance from the front,
 * wrapped so the deck is a loop rather than a line — and CSS derives every
 * transform from it. The timer only runs while the deck is on screen and the
 * pointer is elsewhere: a carousel that keeps cycling in a background tab, or
 * under someone's cursor while they are reading a caption, is an annoyance.
 *
 * NAVIGATION: drag across the cards, the arrows either side, the dots
 * underneath, or the arrow keys once anything in the deck has focus. All four
 * land in pick(), so they behave identically and every one of them resets the
 * beat rather than leaving a card on screen for whatever the timer had left.
 *
 * PLAYBACK RULES, each of which exists because the alternative is worse:
 *
 *   - Only the front card is playable. Tapping a card behind the stack brings
 *     it forward instead of starting audio from something half off-screen.
 *   - Playback is never automatic. These clips are the creator talking over
 *     the product, so they play with sound — and sound only ever starts from
 *     a real click, which is also the only thing browsers will allow unmuted.
 *   - One at a time, page-wide.
 *   - The rotation stops while something is playing and resumes when it stops.
 *     Sliding a video off-stage mid-sentence is the worst version of this.
 *   - Scrolling the deck away, or hiding the tab, stops playback outright.
 */
function initReels(root) {
  root.querySelectorAll('[data-reels]:not([data-reels-done])').forEach((deck) => {
    deck.setAttribute('data-reels-done', '');

    const slides = [...deck.querySelectorAll('[data-slide]')];
    const dots = [...deck.querySelectorAll('[data-goto]')];
    if (!slides.length) return;

    const n = slides.length;
    const interval = Number(deck.dataset.interval || 5200);
    let active = 0;
    let timer = null;
    let visible = false;
    let held = false;
    // Once someone has actually played a clip they are browsing deliberately,
    // and a deck that keeps sliding out from under them is fighting them.
    let touched = false;

    function paint() {
      slides.forEach((el, i) => {
        // Shortest way around the loop, so card 0 slides forward off the last
        // card instead of rewinding through the whole deck.
        let o = i - active;
        if (o > n / 2) o -= n;
        if (o < -n / 2) o += n;

        const abs = Math.abs(o);
        el.style.setProperty('--o', String(o));
        el.style.setProperty('--abs', String(abs));
        el.classList.toggle('is-front', o === 0);
        el.toggleAttribute('data-far', abs > 2);
        // Only the face-on card is announced; the rest are decoration — and a
        // control inside a hidden card must leave the tab order with it.
        el.setAttribute('aria-hidden', o === 0 ? 'false' : 'true');
        // A focusable control inside aria-hidden is a trap for screen reader
        // users. The dots are the keyboard route between cards.
        el.querySelectorAll('a, button').forEach((c) => {
          if (o === 0) c.removeAttribute('tabindex');
          else c.setAttribute('tabindex', '-1');
        });
      });

      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === active);
        d.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    function go(i) {
      const next = ((i % n) + n) % n;
      if (next !== active) stopAllInDeck();
      active = next;
      paint();
    }

    function startRotation() {
      if (timer !== null || !visible || held || touched || reduced()) return;
      // Never rotate out from under something that is talking, or out from
      // under a finger that is mid-drag.
      if (deck.querySelector('.dw-reel-video.is-playing')) return;
      if (deck.querySelector('.is-dragging')) return;
      timer = setInterval(() => go(active + 1), interval);
    }

    function stopRotation() {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    }

    function stopAllInDeck() {
      deck.querySelectorAll('video').forEach((el) => { if (!el.paused) el.pause(); });
    }

    /** Any deliberate move: land on the card and give it a full beat. */
    function pick(i) {
      go(i);
      stopRotation(); startRotation();
    }

    dots.forEach((d, i) => d.addEventListener('click', () => pick(i)));

    deck.querySelectorAll('[data-step]').forEach((arrow) => {
      arrow.addEventListener('click', () => pick(active + Number(arrow.dataset.step)));
    });

    // Arrow keys once anything in the deck has focus — the dots and arrows are
    // all buttons, so tabbing in and steering with the keyboard is the natural
    // next thing to try.
    deck.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') pick(active + 1);
      else if (e.key === 'ArrowLeft') pick(active - 1);
      else return;
      e.preventDefault();
    });

    bindSwipe();

    slides.forEach((slide, i) => {
      const button = slide.querySelector('[data-toggle]');
      const el = slide.querySelector('video');
      if (!button || !el) return;

      // The markup ships "Play <title> by <name>". Keep the subject so the
      // verb can flip without rebuilding the label from the DOM.
      const subject = (button.getAttribute('aria-label') || '').replace(/^Play\s+/, '');

      function state(playing) {
        el.classList.toggle('is-playing', playing);
        slide.classList.toggle('is-playing', playing);
        button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${subject}`);
        if (playing) stopRotation(); else startRotation();
      }

      button.addEventListener('click', () => {
        if (i !== active) { pick(i); return; }
        if (!el.paused) { el.pause(); return; }

        touched = true;
        stopRotation();
        stopAllVideos();
        // Unmuted on purpose: this only ever runs from a click, which is the
        // gesture browsers require before they will let audio out.
        el.muted = false;
        el.play().catch(() => {
          // Autoplay policy, or a file that failed to load. Fall back to muted
          // rather than leaving a button that does nothing when pressed.
          el.muted = true;
          el.play().catch(() => state(false));
        });
      });

      el.addEventListener('play', () => state(true));
      el.addEventListener('pause', () => state(false));
      // Reels do not loop — a 2.5 minute walkthrough restarting forever is not
      // a feature. load() rewinds and puts the poster back, so the card returns
      // to the state it started in rather than freezing on a last frame.
      el.addEventListener('ended', () => { el.load(); state(false); });
    });

    /**
     * Drag or swipe across the stack to move through it.
     *
     * The cards follow the finger via --drag rather than jumping on release,
     * because a carousel that only responds after you let go feels broken.
     *
     * Two things this has to get right:
     *
     *   1. Vertical scrolling must survive. The stage is `touch-action: pan-y`
     *      and the gesture locks to an axis on the first decisive movement, so
     *      a mostly-vertical drag is handed straight back to the page — this
     *      deck sits in a scroll-snapped column and stealing that is fatal.
     *   2. A swipe must not also press play. The frame IS the play button, so
     *      every swipe ends over it; the click that follows is swallowed in
     *      the capture phase when the pointer actually travelled.
     */
    function bindSwipe() {
      const stage = deck.querySelector('[data-stage]');
      if (!stage) return;

      // Far enough that a sloppy tap is still a tap, close enough that a
      // deliberate flick always lands.
      const COMMIT = 45;
      let id = null;
      let startX = 0;
      let startY = 0;
      let axis = null;
      let moved = false;
      let swallowClick = false;

      const setDrag = (px) => stage.style.setProperty('--drag', `${px}px`);

      function end(dx) {
        if (id !== null && stage.hasPointerCapture?.(id)) stage.releasePointerCapture(id);
        id = null;
        stage.classList.remove('is-dragging');
        setDrag(0);
        if (axis === 'x' && Math.abs(dx) > COMMIT) pick(active + (dx < 0 ? 1 : -1));
        else startRotation();
        axis = null;
      }

      stage.addEventListener('pointerdown', (e) => {
        if (e.button > 0) return;
        id = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        axis = null;
        moved = false;
        stopRotation();
      });

      stage.addEventListener('pointermove', (e) => {
        if (e.pointerId !== id) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (!axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axis !== 'x') { id = null; startRotation(); return; }
          stage.setPointerCapture(id);
          stage.classList.add('is-dragging');
        }

        moved = true;
        // Resisted, not free: the deck is a stack being pushed, and a 1:1
        // follow makes it feel like the cards came loose.
        setDrag(dx * 0.55);
      });

      stage.addEventListener('pointerup', (e) => {
        if (e.pointerId !== id) return;
        swallowClick = moved;
        end(e.clientX - startX);
      });

      stage.addEventListener('pointercancel', (e) => {
        if (e.pointerId !== id) return;
        end(0);
      });

      stage.addEventListener('click', (e) => {
        if (!swallowClick) return;
        swallowClick = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);
    }

    for (const ev of ['pointerenter', 'focusin']) {
      deck.addEventListener(ev, () => { held = true; stopRotation(); });
    }
    for (const ev of ['pointerleave', 'focusout']) {
      deck.addEventListener(ev, () => { held = false; startRotation(); });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { stopAllInDeck(); stopRotation(); } else startRotation();
    });

    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      // Scrolling a talking video off-screen and leaving it talking is the
      // single most annoying thing a page can do.
      if (visible) startRotation(); else { stopAllInDeck(); stopRotation(); }
    }, { threshold: 0.25 }).observe(deck);

    paint();
  });
}

/** Every reel on the page, so starting one can silence the rest. */
function stopAllVideos() {
  // Queried by tag and filtered by class rather than with a compound selector:
  // that is the only form the type checker narrows to HTMLVideoElement.
  document.querySelectorAll('video').forEach((el) => {
    if (el.classList.contains('dw-reel-video') && !el.paused) el.pause();
  });
}

// -------------------------------------------------------- hero video ------

/**
 * Start the muted background loop behind the hero.
 *
 * Deliberately not an `autoplay` attribute: under prefers-reduced-motion this
 * declines to start at all and the poster frame stays up, which is the whole
 * point of that setting. It also pauses off-screen — decoding a 1280px loop
 * while someone reads the tier table is battery spent on nothing.
 */
function initHeroVideo(root) {
  root.querySelectorAll('[data-hero-video]:not([data-hero-done])').forEach((el) => {
    el.setAttribute('data-hero-done', '');

    // Set as a property as well as an attribute: the autoplay policy in some
    // browsers reads the property, and an unmuted background loop is refused.
    el.muted = true;

    // Under reduced motion nothing is ever played, so nothing is downloaded
    // either — the poster frame is the whole hero and that is the point.
    if (reduced()) return;

    // Picked once, deliberately not re-picked on resize: rotating a phone
    // should not throw away a loop that is already buffered and start again.
    const small = window.matchMedia('(max-width: 900px)').matches;
    const src = el.dataset[small ? 'srcSm' : 'srcLg'];
    if (!src) return;
    el.src = src;

    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.1 }).observe(el);
  });
}

// ------------------------------------------- parallax / magnetic / tilt ---

function initPointerEffects(root) {
  if (reduced()) return;

  rafTargets.parallax = [...document.querySelectorAll('[data-parallax]')];

  // Magnetic and tilt are pointer-driven, so they are pointless on touch and
  // would only add latency. Gate on a real hover-capable pointer.
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    root.querySelectorAll('[data-magnetic]:not([data-magnetic-done])').forEach((el) => {
      el.setAttribute('data-magnetic-done', '');
      bindMagnetic(el);
    });
    root.querySelectorAll('[data-tilt]:not([data-tilt-done])').forEach((el) => {
      el.setAttribute('data-tilt-done', '');
      bindTilt(el);
    });
  }

  if (rafTargets.parallax.length && rafId === null) {
    rafId = requestAnimationFrame(tick);
  }
}

function bindMagnetic(el) {
  const strength = Number(el.dataset.magnetic || 0.28);

  el.addEventListener('pointermove', (e) => {
    const r = el.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
  });

  el.addEventListener('pointerleave', () => {
    // Spring back rather than snapping — the release is what sells it.
    el.style.transition = `transform var(--dur-base) var(--ease-spring)`;
    el.style.transform = '';
    setTimeout(() => { el.style.transition = ''; }, 300);
  });
}

function bindTilt(el) {
  const max = Number(el.dataset.tilt || 8);

  el.addEventListener('pointermove', (e) => {
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform =
      `perspective(900px) rotateY(${px * max}deg) rotateX(${-py * max}deg) translateZ(0)`;
  });

  el.addEventListener('pointerleave', () => {
    el.style.transition = `transform var(--dur-slow) var(--ease-out)`;
    el.style.transform = '';
    setTimeout(() => { el.style.transition = ''; }, 450);
  });
}

/** Single rAF loop for scroll-driven work, so we never read layout per event. */
function tick() {
  const vh = window.innerHeight;

  for (const el of rafTargets.parallax) {
    const rect = el.getBoundingClientRect();
    if (rect.bottom < -200 || rect.top > vh + 200) continue;
    const speed = Number(el.dataset.parallax || 0.2);
    // Progress through the viewport, -1 (below) .. 1 (above)
    const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
    el.style.setProperty('--parallax-y', `${(-progress * speed * 100).toFixed(2)}px`);
  }

  rafId = requestAnimationFrame(tick);
}

// ------------------------------------------------------ scroll progress ---

function initScrollProgress() {
  const bar = document.getElementById('dw-scroll-progress');
  if (!bar) return;

  const update = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
    bar.style.transform = `scaleX(${(pct / 100).toFixed(4)})`;
  };

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
}

/** Solidify the header once the page has scrolled past the hero. */
function initHeaderState() {
  const header = document.querySelector('.dw-header');
  if (!header) return;

  const update = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 24);
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/**
 * Publish the header's real height as --header-h.
 *
 * scroll-padding-top drives where scroll-snap parks each section and where
 * anchor links land. If it does not match the header exactly, every snapped
 * section shows a band of the previous one above it. Measuring beats guessing:
 * the header changes height between breakpoints, and when the mobile nav opens.
 */
function trackHeaderHeight() {
  const header = document.querySelector('.dw-header');
  if (!header) return;

  const publish = () => {
    const h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--header-h', `${h}px`);
  };

  publish();

  if ('ResizeObserver' in window) {
    new ResizeObserver(publish).observe(header);
  } else {
    window.addEventListener('resize', publish, { passive: true });
  }

  // Webfonts land after first paint and can change the header's height.
  document.fonts?.ready.then(publish);
}

// ----------------------------------------------------------------- api ----

/**
 * Fade images in once they have decoded.
 *
 * Replaces the inline `onload=""` attribute that used to live on every image.
 * That attribute is inline script, and permitting it in CSP means permitting
 * 'unsafe-inline' for all scripts — surrendering most of what CSP protects
 * against for the sake of one class toggle.
 *
 * The `complete` check matters: cached images finish loading before this runs,
 * so a listener alone would leave them stuck at opacity 0 forever.
 */
export function trackImageLoads(root = document) {
  root.querySelectorAll('img.dw-img:not(.is-loaded)').forEach((img) => {
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('is-loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
      // A broken image must not stay invisible — better to show the alt text.
      img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
    }
  });
}

/** Call after every route render. Safe to call repeatedly. */
export function initMotion(root = document) {
  trackImageLoads(root);
  initReveal(root);
  initCounters(root);
  initReels(root);
  initHeroVideo(root);
  initPointerEffects(root);
}

/** Call once at boot for the page-level chrome. */
export function initGlobalMotion() {
  // Document-wide: the header and footer logos render into #shell-header and
  // #shell-footer, which initMotion(outlet) never sees.
  trackImageLoads(document);
  initScrollProgress();
  initHeaderState();
  trackHeaderHeight();
  trackPointer();
}

function trackPointer() {
  if (reduced()) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  window.addEventListener('pointermove', (e) => {
    pointer = { x: e.clientX, y: e.clientY };
    document.documentElement.style.setProperty('--pointer-x', `${e.clientX}px`);
    document.documentElement.style.setProperty('--pointer-y', `${e.clientY}px`);
  }, { passive: true });
}

/**
 * Fire a burst of brand-colored confetti. Reserved for the moments that
 * actually deserve it — a mission approved, a tier unlocked. If it fires for
 * routine actions it stops meaning anything.
 */
export function celebrate(originEl) {
  if (reduced()) return;

  const host = document.createElement('div');
  host.className = 'dw-confetti';
  const rect = originEl?.getBoundingClientRect();
  const cx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
  const cy = rect ? rect.top + rect.height / 2 : window.innerHeight / 3;

  const colors = ['var(--green-500)', 'var(--green-300)', 'var(--purple-500)', 'var(--purple-300)'];

  for (let i = 0; i < 36; i++) {
    const bit = document.createElement('i');
    const angle = (Math.PI * 2 * i) / 36 + Math.random() * 0.4;
    const dist = 90 + Math.random() * 180;
    bit.style.cssText = `
      --tx:${Math.cos(angle) * dist}px;
      --ty:${Math.sin(angle) * dist - 60}px;
      --rot:${Math.random() * 720 - 360}deg;
      --delay:${Math.random() * 90}ms;
      left:${cx}px; top:${cy}px;
      background:${colors[i % colors.length]};
      width:${6 + Math.random() * 5}px;
      height:${8 + Math.random() * 7}px;`;
    host.appendChild(bit);
  }

  document.body.appendChild(host);
  setTimeout(() => host.remove(), 1600);
}
